import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { storeService } from '../../services/storeService';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/Button';
import { CheckIcon, AlertTriangleIcon } from '../../components/ui/Icons';
import { Input } from '../../components/ui/Input';
import { FormField } from '../../components/ui/FormField';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { formatPhoneMask } from '../../utils/phone';
import { formatCpfCnpjMask } from '../../utils/document';
import { loginState } from '../../utils/authRedirect';
import { BABY_GENDER_SELECT_OPTIONS } from '../../utils/gender';
import { PROMOTION_CAMPAIGN_ENABLED } from '../../config/promotionCampaign';
import { ChevronDownIcon, InfoIcon, PlusIcon, TrashIcon, UserIcon } from './CheckoutIcons';
import { GuestInfoErrors, validateGuestInfo } from './guestInfoValidation';
import { CHECKOUT_PRIMARY_ACTION_CLASS, TOUCH_TARGET_CLASS } from './styles';
import { StepBackButton } from './StepBackButton';
import { GuestInfo } from './types';

type LookupState = 'idle' | 'searching' | 'found' | 'not-found';

const CEP_DIGITS = 8;
const MIN_PHONE_DIGITS = 10;
const LOOKUP_DEBOUNCE_MS = 300;
const CEP_NOT_FOUND_MESSAGE = 'Não encontramos esse CEP. Confira o número ou preencha o endereço manualmente.';

const FIELD_CLASS = 'py-2.5';

const DISABLED_FIELD_CLASS =
  'py-2.5 disabled:border-champagne-dark/40 disabled:text-text-medium disabled:bg-champagne/20 disabled:cursor-not-allowed disabled:select-none';

const READONLY_FIELD_CLASS =
  'py-2.5 read-only:border-champagne-dark/50 read-only:text-text-medium read-only:bg-champagne/40 read-only:cursor-not-allowed';

const formatCepMask = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, CEP_DIGITS);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
};

interface GuestInfoStepProps {
  guestInfo: GuestInfo;
  onChange: (info: GuestInfo) => void;
  onNext: () => void;
  backLabel: string;
  onBack: () => void;
}

export function GuestInfoStep({ guestInfo, onChange, onNext, backLabel, onBack }: GuestInfoStepProps) {
  const location = useLocation();
  const [errors, setErrors] = useState<GuestInfoErrors>({});
  const [submitAttempt, setSubmitAttempt] = useState(0);
  const [showOptional, setShowOptional] = useState(false);
  const [lookupState, setLookupState] = useState<LookupState>('idle');
  const [lookupResult, setLookupResult] = useState<{ maskedName: string; city: string } | null>(null);
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState<string | null>(null);
  const confirmed = guestInfo.lookupConfirmed;
  const nextTempBabyId = useRef(-1);
  const formRef = useRef<HTMLDivElement>(null);
  const guestInfoRef = useRef(guestInfo);
  const cepRequestRef = useRef(0);
  guestInfoRef.current = guestInfo;

  useEffect(() => {
    const digits = guestInfo.phone.replace(/\D/g, '');

    if (digits.length < MIN_PHONE_DIGITS) {
      setLookupState('idle');
      setLookupResult(null);
      return;
    }

    setLookupState('searching');
    const timer = setTimeout(async () => {
      try {
        const result = await storeService.lookupCustomer(digits);
        setLookupResult(result);
        setLookupState('found');
      } catch {
        setLookupState('not-found');
        setLookupResult(null);
      }
    }, LOOKUP_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [guestInfo.phone]);

  useEffect(() => {
    if (submitAttempt === 0) return;
    const firstInvalid = formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
    firstInvalid?.focus();
  }, [submitAttempt]);

  const update = (patch: Partial<GuestInfo>) => {
    onChange({ ...guestInfoRef.current, ...patch });
    const touched = Object.keys(patch) as (keyof GuestInfo)[];
    setErrors((prev) => {
      if (!touched.some((key) => prev[key])) return prev;
      const next = { ...prev };
      touched.forEach((key) => delete next[key]);
      return next;
    });
  };

  const clearedAddress = {
    street: '',
    number: '',
    neighborhood: '',
    city: '',
    state: '',
    zipCode: '',
    complement: '',
  };

  const handleConfirm = () => {
    onChange({
      ...guestInfo,
      ...clearedAddress,
      lookupConfirmed: true,
      city: lookupResult?.city ?? '',
    });
  };

  const clearLookupData = (info: GuestInfo): GuestInfo => ({
    ...info,
    ...clearedAddress,
    lookupConfirmed: false,
  });

  const handleDeny = () => {
    if (confirmed) onChange(clearLookupData(guestInfo));
    setLookupState('not-found');
  };

  const handleChangePhone = () => {
    onChange({ ...clearLookupData(guestInfo), phone: '' });
    setLookupState('idle');
    setLookupResult(null);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextPhone = formatPhoneMask(e.target.value);
    const next = confirmed ? clearLookupData(guestInfo) : guestInfo;
    onChange({ ...next, phone: nextPhone });
    setErrors((prev) => ({ ...prev, phone: undefined }));
  };

  const addBaby = () => {
    update({
      babies: [...guestInfo.babies, { id: nextTempBabyId.current--, name: '', birthdate: '', gender: '' }],
    });
  };

  const removeBaby = (id: number) => {
    update({ babies: guestInfo.babies.filter((b) => b.id !== id) });
  };

  const updateBabyField = (id: number, field: 'name' | 'birthdate' | 'gender', value: string) => {
    update({ babies: guestInfo.babies.map((b) => (b.id === id ? { ...b, [field]: value } : b)) });
  };

  const searchCep = async (digits: string) => {
    const requestId = ++cepRequestRef.current;
    setCepLoading(true);
    setCepError(null);
    try {
      const address = await storeService.lookupCep(digits);
      if (requestId !== cepRequestRef.current) return;
      const current = guestInfoRef.current;
      update({
        street: address.street || current.street,
        neighborhood: address.neighborhood || current.neighborhood,
        city: address.city || current.city,
        state: address.state || current.state,
      });
    } catch {
      if (requestId === cepRequestRef.current) setCepError(CEP_NOT_FOUND_MESSAGE);
    } finally {
      if (requestId === cepRequestRef.current) setCepLoading(false);
    }
  };

  const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = formatCepMask(e.target.value);
    update({ zipCode: masked });
    setCepError(null);
    const digits = masked.replace(/\D/g, '');
    if (digits.length === CEP_DIGITS) {
      searchCep(digits);
    } else {
      cepRequestRef.current++;
      setCepLoading(false);
    }
  };

  const handleSubmit = () => {
    const newErrors = validateGuestInfo(guestInfo);

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setSubmitAttempt((n) => n + 1);
      return;
    }

    setErrors({});
    onNext();
  };

  const errorMessages = Object.values(errors).filter((message): message is string => Boolean(message));
  const fieldsEnabled = lookupState === 'not-found';
  const zipDigits = guestInfo.zipCode.replace(/\D/g, '');

  return (
    <div>
      {!PROMOTION_CAMPAIGN_ENABLED && (
        <div className="flex items-start gap-2.5 sm:gap-3 bg-champagne/60 rounded-xl p-3 sm:p-3.5 mb-4 sm:mb-5 border border-champagne-dark/30">
          <div className="w-8 h-8 rounded-full bg-chocolate/10 flex items-center justify-center flex-shrink-0 text-chocolate-dark">
            <UserIcon className="w-4 h-4" />
          </div>
          <p className="text-sm text-text-dark pt-1">
            Já tem conta?{' '}
            <Link
              to="/login"
              state={loginState(location)}
              className="text-chocolate-dark font-semibold underline hover:text-text-dark"
            >
              Faça login
            </Link>{' '}
            para acompanhar seus pedidos.
          </p>
        </div>
      )}

      <div ref={formRef} data-clarity-mask="true" className="bg-white rounded-xl shadow-sm p-5 mb-4">
        <h2 className="text-base font-semibold text-text-dark mb-4">Seus dados</h2>

        <div className="flex flex-col gap-4">
          <FormField label="Celular" required>
            <div className="relative">
              <Input
                type="tel"
                value={guestInfo.phone}
                onChange={handlePhoneChange}
                placeholder="(00) 00000-0000"
                inputMode="numeric"
                autoComplete="tel"
                readOnly={confirmed}
                error={errors.phone}
                className={`pr-9 ${READONLY_FIELD_CLASS}`}
              />
              {lookupState === 'searching' && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Spinner size="sm" />
                </div>
              )}
              {(lookupState === 'found' || confirmed) && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-olivine-dark">
                  <CheckIcon className="w-4 h-4" />
                </div>
              )}
            </div>
            {lookupState === 'idle' && guestInfo.phone.replace(/\D/g, '').length < MIN_PHONE_DIGITS && (
              <p className="text-sm text-text-medium mt-1">Comece pelo celular: se você já comprou aqui, preenchemos o resto para você.</p>
            )}
          </FormField>

          {lookupState === 'found' && !confirmed && lookupResult && (
            <div className="bg-champagne/60 border border-champagne-dark/30 rounded-xl p-4">
              <div className="flex items-start gap-2.5 mb-3">
                <div className="w-8 h-8 rounded-full bg-chocolate/10 flex items-center justify-center flex-shrink-0 text-chocolate-dark">
                  <UserIcon className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-text-dark">Cadastro encontrado!</p>
                  <p className="text-sm text-text-medium mt-0.5">
                    Encontramos um cadastro para <span className="font-medium text-text-dark">{lookupResult.maskedName}</span> em{' '}
                    <span className="font-medium text-text-dark">{lookupResult.city}</span>. Este é você?
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={handleConfirm} className={`flex-1 ${TOUCH_TARGET_CLASS} ${CHECKOUT_PRIMARY_ACTION_CLASS}`}>
                  Sim, sou eu
                </Button>
                <Button variant="outline" onClick={handleDeny} className={`flex-1 ${TOUCH_TARGET_CLASS}`}>
                  Não, continuar
                </Button>
              </div>
            </div>
          )}

          {confirmed && (
            <>
              <div className="bg-champagne/60 border border-champagne-dark/40 rounded-xl p-4 flex items-start gap-2.5">
                <InfoIcon className="w-5 h-5 text-chocolate-dark flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-text-dark">
                    Vamos usar o endereço do seu cadastro. Se precisar mudar, é só avisar a vendedora no WhatsApp.
                  </p>
                  <div className="flex flex-wrap gap-x-4 mt-1">
                    <button
                      type="button"
                      onClick={handleChangePhone}
                      className="min-h-[44px] text-sm font-semibold text-chocolate-dark underline hover:text-text-dark"
                    >
                      Usar outro celular
                    </button>
                    {!PROMOTION_CAMPAIGN_ENABLED && (
                      <Link
                        to="/login"
                        state={loginState(location)}
                        className="inline-flex items-center min-h-[44px] text-sm font-semibold text-chocolate-dark underline hover:text-text-dark"
                      >
                        Já tem conta? Faça login
                      </Link>
                    )}
                  </div>
                </div>
              </div>

              <FormField label="Como podemos te chamar?" required hint="Usamos esse nome na mensagem para a vendedora.">
                <Input
                  type="text"
                  value={guestInfo.name}
                  onChange={(e) => update({ name: e.target.value })}
                  placeholder="Seu nome"
                  autoComplete="nickname"
                  error={errors.name}
                  className={FIELD_CLASS}
                />
              </FormField>
            </>
          )}

          {!confirmed && (
            <>
              <FormField label="Nome" required>
                <Input
                  type="text"
                  value={guestInfo.name}
                  onChange={(e) => update({ name: e.target.value })}
                  placeholder="Nome e Sobrenome"
                  autoComplete="name"
                  disabled={!fieldsEnabled}
                  error={errors.name}
                  className={DISABLED_FIELD_CLASS}
                />
              </FormField>

              <FormField label="CEP">
                <div className="flex gap-2">
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={formatCepMask(guestInfo.zipCode)}
                    onChange={handleCepChange}
                    placeholder="00000-000"
                    autoComplete="postal-code"
                    disabled={!fieldsEnabled}
                    className={`flex-1 ${DISABLED_FIELD_CLASS}`}
                  />
                  <Button
                    onClick={() => searchCep(zipDigits)}
                    disabled={!fieldsEnabled || cepLoading || zipDigits.length !== CEP_DIGITS}
                    loading={cepLoading}
                    className={`${TOUCH_TARGET_CLASS} whitespace-nowrap ${CHECKOUT_PRIMARY_ACTION_CLASS}`}
                  >
                    Buscar
                  </Button>
                </div>
                {cepError ? (
                  <p role="status" className="text-sm text-danger mt-1">
                    {cepError}
                  </p>
                ) : (
                  <p className="text-sm text-text-medium mt-1">Preenche rua, bairro, cidade e estado automaticamente</p>
                )}
              </FormField>

              <FormField label="Rua" required>
                <Input
                  type="text"
                  value={guestInfo.street}
                  onChange={(e) => update({ street: e.target.value })}
                  placeholder="Sua rua"
                  autoComplete="address-line1"
                  disabled={!fieldsEnabled}
                  error={errors.street}
                  className={DISABLED_FIELD_CLASS}
                />
              </FormField>

              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <FormField label="Número" required>
                  <Input
                    type="text"
                    value={guestInfo.number}
                    onChange={(e) => update({ number: e.target.value })}
                    placeholder="Nº"
                    autoComplete="off"
                    disabled={!fieldsEnabled}
                    error={errors.number}
                    className={DISABLED_FIELD_CLASS}
                  />
                </FormField>

                <FormField label="Complemento">
                  <Input
                    type="text"
                    value={guestInfo.complement}
                    onChange={(e) => update({ complement: e.target.value })}
                    placeholder="Apto, bloco, etc."
                    autoComplete="address-line2"
                    disabled={!fieldsEnabled}
                    className={DISABLED_FIELD_CLASS}
                  />
                </FormField>
              </div>

              <FormField label="Bairro" required>
                <Input
                  type="text"
                  value={guestInfo.neighborhood}
                  onChange={(e) => update({ neighborhood: e.target.value })}
                  placeholder="Seu bairro"
                  autoComplete="address-level3"
                  disabled={!fieldsEnabled}
                  error={errors.neighborhood}
                  className={DISABLED_FIELD_CLASS}
                />
              </FormField>

              <div className="grid grid-cols-[1fr_6rem] gap-2 sm:gap-3">
                <FormField label="Cidade" required>
                  <Input
                    type="text"
                    value={guestInfo.city}
                    onChange={(e) => update({ city: e.target.value })}
                    placeholder="Sua cidade"
                    autoComplete="address-level2"
                    disabled={!fieldsEnabled}
                    error={errors.city}
                    className={DISABLED_FIELD_CLASS}
                  />
                </FormField>

                <FormField label="UF" required>
                  <Input
                    type="text"
                    value={guestInfo.state}
                    onChange={(e) => update({ state: e.target.value.toUpperCase().slice(0, 2) })}
                    placeholder="UF"
                    maxLength={2}
                    autoComplete="address-level1"
                    disabled={!fieldsEnabled}
                    error={errors.state}
                    className={DISABLED_FIELD_CLASS}
                  />
                </FormField>
              </div>
            </>
          )}
        </div>
      </div>

      {!confirmed && (
        <div className={['bg-white rounded-xl shadow-sm overflow-hidden mb-4', !fieldsEnabled ? 'opacity-40 pointer-events-none' : ''].join(' ')}>
          <button
            type="button"
            aria-expanded={showOptional}
            onClick={() => setShowOptional(!showOptional)}
            disabled={!fieldsEnabled}
            className="w-full min-h-[52px] flex items-center justify-between px-5 py-3 text-left hover:bg-champagne/30 transition-colors disabled:cursor-not-allowed"
          >
            <div className="flex items-center gap-2.5">
              <PlusIcon className="w-4 h-4 text-chocolate-dark" />
              <span className="text-sm font-medium text-text-dark">Dados adicionais</span>
              <span className="text-sm text-text-medium">(opcional)</span>
            </div>
            <ChevronDownIcon
              className={`w-4 h-4 text-text-medium transition-transform duration-200 ${showOptional ? 'rotate-180' : ''}`}
            />
          </button>

          {showOptional && (
            <div className="px-5 pb-5 pt-1 border-t border-champagne-dark/20">
              <p className="text-sm text-text-medium mb-4">
                Preencha para uma experiência mais personalizada. Se preferir, a vendedora pode completar depois.
              </p>

              <div className="flex flex-col gap-4">
                <FormField label="E-mail">
                  <Input
                    type="email"
                    value={guestInfo.email}
                    onChange={(e) => update({ email: e.target.value })}
                    placeholder="seu@email.com"
                    autoComplete="email"
                    className={FIELD_CLASS}
                  />
                </FormField>

                <FormField label="CPF/CNPJ">
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={guestInfo.cpfCnpj}
                    onChange={(e) => update({ cpfCnpj: formatCpfCnpjMask(e.target.value) })}
                    placeholder="000.000.000-00"
                    autoComplete="off"
                    className={FIELD_CLASS}
                  />
                </FormField>

                <div>
                  <p className="text-sm font-medium text-text-medium mb-2">Filhos(as)</p>

                  {guestInfo.babies.length > 0 && (
                    <div className="flex flex-col gap-3 mb-3">
                      {guestInfo.babies.map((baby) => (
                        <div
                          key={baby.id}
                          className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 sm:gap-3 sm:items-end p-3 rounded-xl bg-champagne/40 border border-champagne-dark/20"
                        >
                          <FormField label="Nome">
                            <Input
                              type="text"
                              value={baby.name}
                              onChange={(e) => updateBabyField(baby.id, 'name', e.target.value)}
                              placeholder="Nome do filho(a)"
                              autoComplete="off"
                              className={FIELD_CLASS}
                            />
                          </FormField>
                          <FormField label="Nascimento">
                            <Input
                              type="date"
                              value={baby.birthdate}
                              onChange={(e) => updateBabyField(baby.id, 'birthdate', e.target.value)}
                              autoComplete="off"
                              className={FIELD_CLASS}
                            />
                          </FormField>
                          <FormField label="Gênero">
                            <SearchableSelect
                              options={BABY_GENDER_SELECT_OPTIONS}
                              value={baby.gender ? Number(baby.gender) : undefined}
                              onChange={(id) => updateBabyField(baby.id, 'gender', id != null ? String(id) : '')}
                              placeholder="Não informado"
                              allLabel="Não informado"
                            />
                          </FormField>
                          <button
                            type="button"
                            onClick={() => removeBaby(baby.id)}
                            aria-label="Remover filho(a)"
                            className={`${TOUCH_TARGET_CLASS} flex items-center justify-center rounded-lg text-danger hover:bg-danger/10 transition-colors justify-self-start sm:justify-self-auto`}
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={addBaby}
                    className="inline-flex items-center gap-1.5 min-h-[44px] text-sm font-semibold text-chocolate-dark hover:text-text-dark transition-colors"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Adicionar filho(a)
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {errorMessages.length > 0 && (
        <div
          role="alert"
          aria-live="assertive"
          className="flex items-start gap-3 rounded-xl border border-danger/50 bg-danger/5 p-4 mb-4"
        >
          <AlertTriangleIcon className="w-5 h-5 text-danger shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-text-dark">Confira os campos abaixo para continuar</p>
            <ul className="mt-1 list-disc pl-5 text-sm text-text-dark">
              {errorMessages.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <Button
        onClick={handleSubmit}
        size="lg"
        className={`w-full ${CHECKOUT_PRIMARY_ACTION_CLASS}`}
        disabled={!confirmed && (lookupState === 'searching' || lookupState === 'found')}
      >
        {lookupState === 'searching' ? 'Verificando...' : 'Continuar'}
      </Button>
      <StepBackButton label={backLabel} onClick={onBack} className="mt-1" />
    </div>
  );
}
